import {
  Box,
  Typography as Typo,
  styled,
  TypographyProps,
  FormControl,
  FormHelperText as MuiFormHelperText,
  TextField,
} from '@mui/material';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useFormik} from 'formik';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {enqueueSnackbar} from 'notistack';
import {showErrorToast} from '@jobseeker/common-core/utils';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import userService from '@jobseeker/common-core/services/user.service';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {validationSchemaOtp} from '../../../../helpers/formValidator';
import {openUrl} from '@jobseeker/common-core/utils/common';

const Typography = styled(Typo)<TypographyProps>(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
  bottom: -18,
}));

const StyledTextField = styled(TextField)(() => ({
  borderRadius: '32px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '32px',
    border: '1px solid var(--dividers, rgba(126, 126, 126, 0.20))',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    height: '40px',
    padding: '0 14px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '500',
  },
}));

interface OtpComponentProps {
  phone?: string;
  countryCode?: number;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
  isForChangeNumber: boolean;
  isForVerification?: boolean;
}

export const OtpComponent = ({
  phone: propPhone,
  countryCode: propCountryCode,
  setDialogOpen,
  isForChangeNumber,
  isForVerification = false,
}: OtpComponentProps) => {
  const TIME_LIMIT_FOR_RESEND = 30;
  const [seconds, setSeconds] = useState(TIME_LIMIT_FOR_RESEND);
  const timerRef = useRef<any>();

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setSeconds(count => count - 1);
    }, 1000);
  };

  useEffect(() => {
    if (seconds <= 0) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (seconds == TIME_LIMIT_FOR_RESEND) {
      startTimer();
    }
    console.log(seconds);
  }, [seconds]);

  const formattedDuration = useMemo(() => {
    const minutes = Math.floor(seconds / 60);
    let secnds = seconds;
    if (minutes > 0) {
      secnds = secnds - minutes * 60;
    }
    return `${(minutes > 9 ? '' : '0') + minutes}:${
      (secnds > 9 ? '' : '0') + secnds
    }`;
  }, [seconds]);

  const {profile} = useSelector((state: AppState) => state.user);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      otp: '',
      phone: propPhone ?? profile.phone,
      countryCode: propCountryCode ?? profile.countryCode,
    },
    validationSchema: validationSchemaOtp,
    onSubmit: async (values: any) => {
      let resp;
      if (isForVerification) {
        resp = await userService.verifyContactDetails(
          {otp: values.otp},
          'phone'
        );
      } else {
        resp = await userService.twoFaVerify(values);
      }

      if (resp?.error) {
        formik.setFieldError('otp', resp.error.message);
        return;
      }

      const profilePayload = {
        phone: values.phone,
        countryCode: values.countryCode,
        ...(isForVerification
          ? {verifiedNumber: 1}
          : {settings: {accountSettings: {twoFactorAuth: 1}}}),
      };

      dispatch({
        type: 'User/set_user_profile',
        payload: profilePayload,
      });

      const successMessage = isForVerification
        ? ''
        : isForChangeNumber
          ? 'Mobile Number changed successfully'
          : '2-step verification successfully enabled';

      if (successMessage) {
        enqueueSnackbar(successMessage, {variant: 'success'});
      }

      setDialogOpen(false);
    },
    validateOnChange: false,
  });

  const hasError = formik.touched.otp && Boolean(formik.errors.otp);
  const isInputEmpty = formik.values.otp.trim() === '';

  const handleResendOtp = async () => {
    const phone = propPhone ?? profile.phone;
    const countryCode = propCountryCode ?? profile.countryCode;

    const response = await userService.sendOtp({
      phone,
      countryCode,
      isForVerification,
    });
    if (response.error) {
      showErrorToast(response.error, response);
      return;
    }
    setSeconds(TIME_LIMIT_FOR_RESEND);

    enqueueSnackbar('Code resent successfully', {variant: 'success'});
  };

  const redirectTo = (url: string) => () => openUrl(url);
  const openPersonalEdit = redirectTo('/registration/addPersonalDetails');

  return (
    <Box
      component="form"
      noValidate
      onSubmit={event => {
        event.preventDefault();
        formik.handleSubmit();
      }}
    >
      {!isForChangeNumber && !isForVerification && (
        <Typography
          mt={2}
          mb={1}
          sx={{fontSize: 14, fontWeight: 400, color: 'secondary.main'}}
        >
          {`Please enter the 6-digit code sent to +${
            propCountryCode ?? profile.countryCode
          }-${
            propPhone ?? profile.phone
          } to complete your 2-step verification setup`}
        </Typography>
      )}
      <FormControl
        sx={{width: '100%', mt: isForVerification ? 1.5 : 0.5}}
        error={formik.touched.otp && Boolean(formik.errors.otp)}
      >
        <StyledTextField
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (
              !/[0-9]/.test(event.key) &&
              event.key !== 'Backspace' &&
              event.key !== 'Enter'
            ) {
              event.preventDefault();
            }

            if (formik.values.otp.length >= 6 && event.key !== 'Backspace') {
              event.preventDefault();
            }

            if (event.key === 'Enter') {
              formik.handleSubmit();
            }
          }}
          variant="outlined"
          type="text"
          name="otp"
          value={formik.values.otp}
          onChange={e => {
            formik.setFieldTouched('otp', false);
            formik.handleChange(e);
          }}
          error={formik.touched.otp && Boolean(formik.errors.otp)}
          placeholder="Enter OTP"
        />
        {formik?.touched?.otp && Boolean(formik?.errors?.otp) && (
          <FormHelperText>
            <>{formik?.errors?.otp || ''}</>
          </FormHelperText>
        )}
      </FormControl>
      <Box display="flex" justifyContent="space-between" ml={2}>
        <Typography
          fontSize={10}
          fontWeight={500}
          sx={{
            padding: '0px',
            mt: '2px',
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        ></Typography>

        <Typography
          variant="subtitle3"
          color={seconds > 0 ? 'text.primary' : 'primary'}
          sx={{
            padding: '0px',
            mt: '2px',
            display: 'flex',
            justifyContent: 'flex-start',
            '&:hover': {cursor: 'pointer', backgroundColor: 'transparent'},
          }}
          onClick={async () => {
            if (seconds === 0) {
              await handleResendOtp();
            }
          }}
        >
          {seconds > 0 ? `Resend Code in ${seconds}s` : 'Resend Code'}
        </Typography>
      </Box>
      {isForVerification ? (
        <Box
          display={'flex'}
          justifyContent={{xxs: 'space-between', md: 'flex-start'}}
          gap={1.5}
          mt={2}
          mb={2}
        >
          <Button
            sx={{px: 3, py: 1.25}}
            disabled={hasError || isInputEmpty}
            onClick={formik.handleSubmit}
            type="submit"
            color="secondary"
            variant="contained"
          >
            Confirm
          </Button>
          <Box sx={{display: {xxs: 'block', md: 'flex'}}}>
            <Typography
              variant="body3"
              color="text.primary"
              lineHeight={'16px'}
            >
              Not the right number?&nbsp;
            </Typography>
            <Typography
              onClick={openPersonalEdit}
              sx={{
                cursor: 'pointer',
                padding: '0px',
                fontSize: '12px',
                lineHeight: '16px',
                fontWeight: '500',
                justifyContent: 'flex-end',
                color: 'var(--brand-color, #49966E)',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              Change
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box display="flex" gap={1.5} mt={2} mb={2}>
          <Button
            sx={{px: 3, py: 1}}
            disabled={hasError}
            onClick={formik.handleSubmit}
            type="submit"
            color="secondary"
            variant="contained"
          >
            Continue
          </Button>
        </Box>
      )}
    </Box>
  );
};
